import React, { Component, Fragment } from "react";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import GlobalMessage from "../GlobalMessage";
import LoadingMsgBar from "../LoadingMsgBar";
import ProviderSearchTable from "./ProviderSearchTable";
import AuthenticationService from "../../Services/AuthenticationService";
import { isNumeric } from "../../utils/Validator";
import Excel from "exceljs";
import FileSaver from "file-saver";
import "./ProviderSearch.css";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  button: {
    margin: theme.spacing(1),
    cursor: "pointer",
    color: "#0f197d",
  },
});

const providerTypeOptions = [
  { label: "Any", value: "Any" },
  { label: "Physician", value: "P" },
  { label: "Facility (Ancillary & Hospital)", value: "F" },
];

const providerTinStatusOptions = [
  { label: "Active", value: "Active" },
  { label: "Any", value: "Any" },
];

const providerPlanStatusOptions = [
  { label: "Any", value: "Any" },
  { label: "Active", value: "Active" },
];

const getProviderTypeLabel = (providerTypeCode) => {
  switch (providerTypeCode) {
    case "P":
      return "Physician";
    case "F":
      return "Facility (Ancillary & Hospital)";
    case "A":
      return "Ancillary";
    case "H":
      return "Hospital";
    default:
      return providerTypeCode || "";
  }
};

window.analytics = { pageName: "Provider Search" };

class ProviderSearch extends Component {
  constructor(props) {
    super(props);

    this.state = {
      searchBy: "TIN",
      tin: "",
      providerId: "",
      providerType: "Any",
      providerTinStatus: "Active",
      providerPlanStatus: "Active",
      tinError: "",
      providerIdError: "",
      providerTypeError: "",
      providerPlanStatusError: "",
      globalError: "",
      progressMessage: "",
      providers: [],
      filteredProviders: [],
      showResults: false,
      isSearchInProgress: false,
      isDownloadEnabled: true,
      filtered: false,
    };

    this.cancelTokenSource = null;
  }

  componentWillUnmount() {
    this.cancelSearchRequest();
  }

  onChange = (event) => {
    const {
      target: { name, value },
    } = event;

    this.setState({ [name]: value });
  };

  clearFieldErrors = () => {
    this.setState({
      tinError: "",
      providerIdError: "",
      providerTypeError: "",
      providerPlanStatusError: "",
      globalError: "",
    });
  };

  cancelSearchRequest = () => {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("Provider search cancelled.");
      this.cancelTokenSource = null;
    }
  };

  sanitizeAndSetState = () => {
    const tin = this.state.tin.trim();
    const providerId = this.state.providerId.trim();

    this.setState({ tin, providerId });

    return {
      searchBy: this.state.searchBy,
      tin,
      providerId,
      providerType: this.state.providerType,
      providerTinStatus: this.state.providerTinStatus,
      providerPlanStatus: this.state.providerPlanStatus,
    };
  };

  validate = (sanitized) => {
    const { searchBy, tin, providerId, providerType, providerPlanStatus } = sanitized;

    let hasError = false;
    let globalError = "";
    const nextState = {
      tinError: "",
      providerIdError: "",
      providerTypeError: "",
      providerPlanStatusError: "",
    };

    if (searchBy === "TIN") {
      if (tin === "") {
        globalError =
          'An error has occurred. Please address all fields marked with "Error".';
        nextState.tinError = "Error: Enter a Tax ID Number (TIN)";
        hasError = true;
      } else if (!isNumeric(tin) || tin.length !== 9) {
        globalError =
          'An error has occurred. Please address all fields marked with "Error".';
        nextState.tinError = "Error: Enter a 9-digit Tax ID Number (TIN)";
        hasError = true;
      }
    }

    if (searchBy === "PROVIDER_ID") {
      if (providerId === "") {
        globalError =
          'An error has occurred. Please address all fields marked with "Error".';
        nextState.providerIdError = "Error: Enter a Provider ID Number";
        hasError = true;
      } else if (!isNumeric(providerId)) {
        globalError =
          'An error has occurred. Please address all fields marked with "Error".';
        nextState.providerIdError = "Error: Enter a valid Provider ID Number";
        hasError = true;
      }
    }

    if (providerType === undefined || providerType === null) {
      nextState.providerTypeError = "Error: Select a Provider Type";
      globalError =
        'An error has occurred. Please address all fields marked with "Error".';
      hasError = true;
    }

    if (providerPlanStatus === undefined || providerPlanStatus === null) {
      nextState.providerPlanStatusError = "Error: Select a Provider-Plan Status";
      globalError =
        'An error has occurred. Please address all fields marked with "Error".';
      hasError = true;
    }

    this.setState({ ...nextState, globalError });

    return !hasError;
  };

  getSearchPayload = (sanitized) => {
    const payload = {};

    if (sanitized.searchBy === "TIN") {
      payload.prvTaxId = sanitized.tin;
      payload.providerTinStatus = sanitized.providerTinStatus;
    }

    if (sanitized.searchBy === "PROVIDER_ID") {
      payload.prvId = Number(sanitized.providerId);
    }

    payload.pstatStatus = sanitized.providerPlanStatus;
    payload.prvProviderType = sanitized.providerType;

    return payload;
  };

  executeSearch = (sanitized) => {
    const payload = this.getSearchPayload(sanitized);

    this.cancelSearchRequest();
    this.cancelTokenSource = AuthenticationService.getCancelTokenSource();

    this.setState({
      isSearchInProgress: true,
      progressMessage: "Searching for providers...",
      providers: [],
      filteredProviders: [],
      showResults: false,
      filtered: false,
      isDownloadEnabled: true,
      globalError: "",
    });

    AuthenticationService.searchProviders(payload, this.cancelTokenSource.token)
      .then((response) => {
        const providers = Array.isArray(response.data) ? response.data : [];
        const displayProviders = providers.map((provider) => ({
          ...provider,
          prvProviderTypeDisplay: getProviderTypeLabel(provider.prvProviderType),
        }));

        this.setState({
          providers: displayProviders,
          filteredProviders: displayProviders,
          showResults: displayProviders.length > 0,
          isSearchInProgress: false,
          progressMessage: "",
          globalError:
            displayProviders.length === 0 ? "No providers found." : "",
        });
      })
      .catch((error) => {
        if (AuthenticationService.isCancelRequest(error)) {
          return;
        }

        this.setState({
          isSearchInProgress: false,
          progressMessage: "",
          showResults: false,
          globalError:
            "Error: There was an error retrieving data. Please try again or contact Customer Service.",
        });
      })
      .finally(() => {
        this.cancelTokenSource = null;
      });
  };

  handleSearch = () => {
    this.clearFieldErrors();

    const sanitized = this.sanitizeAndSetState();

    if (!this.validate(sanitized)) {
      return;
    }

    this.executeSearch(sanitized);
  };

  handleReset = () => {
    this.cancelSearchRequest();

    this.setState({
      searchBy: "TIN",
      tin: "",
      providerId: "",
      providerType: "Any",
      providerTinStatus: "Active",
      providerPlanStatus: "Active",
      tinError: "",
      providerIdError: "",
      providerTypeError: "",
      providerPlanStatusError: "",
      globalError: "",
      progressMessage: "",
      providers: [],
      filteredProviders: [],
      showResults: false,
      isSearchInProgress: false,
      isDownloadEnabled: true,
      filtered: false,
    });
  };

  setFilter = (filteredProviders) => {
    this.setState({
      filteredProviders,
      filtered: true,
    });
  };

  getExportData = () => {
    if (this.state.filtered && this.state.filteredProviders.length > 0) {
      return this.state.filteredProviders;
    }

    return this.state.providers;
  };

  getDate = () => {
    const today = Date.now();
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(today);
  };

  handleDownload = () => {
    this.setState({
      isDownloadEnabled: false,
      progressMessage: "Please wait while we download Provider Search Table.",
    });

    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet("Provider Search");

    worksheet.mergeCells("C1", "D1");
    worksheet.getCell("C1").value = "Provider Search";
    worksheet.getCell("C1").font = { bold: true, size: 16 };

    worksheet.getRow(3).values = ["Date", this.getDate()];
    worksheet.getCell("A3").font = { bold: true };

    worksheet.getRow(6).values = [
      "Tax ID Number (TIN)",
      "Provider ID Number",
      "Provider Name",
      "Provider Type",
      "Provider-Plan Status",
    ];

    worksheet.columns = [
      { key: "prvTaxId", width: 22 },
      { key: "prvId", width: 20 },
      { key: "prvName", width: 40 },
      { key: "prvProviderTypeDisplay", width: 28 },
      { key: "pstatStatus", width: 18 },
    ];

    this.getExportData().forEach((provider) => {
      worksheet.addRow(provider);
    });

    worksheet.getRow(6).eachCell((cell) => {
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "double" },
      };
    });

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        FileSaver.saveAs(new Blob([buffer]), "ProviderSearch.xlsx");
      })
      .catch(() => {
        this.setState({
          globalError:
            "Error: There was an error downloading data. Please try again or contact Customer Service.",
        });
      })
      .finally(() => {
        this.setState({
          isDownloadEnabled: true,
          progressMessage: "",
        });
      });
  };

  render() {
    return (
      <Fragment>
        <div className="App-content Provider-search">
          <h1 className="App-page-label">Provider Search</h1>

          <LoadingMsgBar loadMsg={this.state.progressMessage} />
          <GlobalMessage message={this.state.globalError} />

          <div className="search-by-row">
            <FormControl component="fieldset">
              <FormLabel component="legend">Search By</FormLabel>
              <RadioGroup
                row
                name="searchBy"
                value={this.state.searchBy}
                onChange={this.onChange}
              >
                <FormControlLabel
                  value="TIN"
                  control={<Radio color="primary" />}
                  label="Tax ID Number (TIN)"
                />
                <FormControlLabel
                  value="PROVIDER_ID"
                  control={<Radio color="primary" />}
                  label="Provider ID Number"
                />
              </RadioGroup>
            </FormControl>
          </div>

          <div className="search-form-row">
            {this.state.searchBy === "TIN" && (
              <TextField
                name="tin"
                label="Tax ID Number (TIN)"
                autoFocus
                className="search-field"
                inputProps={{ maxLength: 9 }}
                margin="normal"
                variant="outlined"
                value={this.state.tin}
                onChange={this.onChange}
                error={this.state.tinError !== ""}
                helperText={this.state.tinError}
              />
            )}

            {this.state.searchBy === "TIN" && (
              <TextField
                select
                name="providerTinStatus"
                label="Provider-TIN Status"
                className="search-field"
                margin="normal"
                variant="outlined"
                value={this.state.providerTinStatus}
                onChange={this.onChange}
              >
                {providerTinStatusOptions.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {this.state.searchBy === "PROVIDER_ID" && (
              <TextField
                name="providerId"
                label="Provider ID Number"
                autoFocus
                className="search-field"
                inputProps={{ maxLength: 8 }}
                margin="normal"
                variant="outlined"
                value={this.state.providerId}
                onChange={this.onChange}
                error={this.state.providerIdError !== ""}
                helperText={this.state.providerIdError}
              />
            )}

            <TextField
              select
              name="providerType"
              label="Provider Type"
              className="search-field"
              margin="normal"
              variant="outlined"
              value={this.state.providerType}
              onChange={this.onChange}
              error={this.state.providerTypeError !== ""}
              helperText={this.state.providerTypeError}
            >
              {providerTypeOptions.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              name="providerPlanStatus"
              label="Provider-Plan Status"
              className="search-field"
              margin="normal"
              variant="outlined"
              value={this.state.providerPlanStatus}
              onChange={this.onChange}
              error={this.state.providerPlanStatusError !== ""}
              helperText={this.state.providerPlanStatusError}
            >
              {providerPlanStatusOptions.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div className="button-row">
            <Button
              onClick={this.handleSearch}
              disabled={this.state.isSearchInProgress}
            >
              SEARCH
            </Button>
            <Button variant="outlined" onClick={this.handleReset}>
              RESET
            </Button>
          </div>

          {this.state.showResults ? (
            <div>
              <div className="download-row">
                <Button
                  disabled={!this.state.isDownloadEnabled}
                  variant="outlined"
                  onClick={this.handleDownload}
                >
                  DOWNLOAD
                </Button>
              </div>

              <ProviderSearchTable
                providers={this.state.filteredProviders}
                unfilteredProviders={this.state.providers}
                setFilter={this.setFilter}
              />
            </div>
          ) : null}
        </div>
      </Fragment>
    );
  }
}

export default withStyles(styles)(ProviderSearch);
